import React, { forwardRef } from 'react';
import ringArt from '../../../assets/img/portal-ring.png';
import { RING } from '../../wall/wallTokens';
import { TicketIcon, PinIcon } from './icons';

/**
 * The event and the venue on the card — one component, eleven shared styles (handoff §5.4):
 * CARD · MINI · STUB · TAG · DATE · PIN · TICKET · NEON · POSTER · STICKER · SCRIPT. The markup
 * is the prototype's; only the words and the glyph change between the two kinds.
 *
 * `data` for an event: { title, short, mon, day, time, place, meta }. For a venue:
 * { title, meta (the address), city, mon, day, time (the Portal's own window) }.
 */
const ring6 = { background: RING, backgroundSize: '200% 100%', animation: 'prcRingSlide 6s linear infinite' };
const spin = { animation: 'prcSpin 6s linear infinite' };
const ringBg = { backgroundImage: `url(${ringArt})`, backgroundSize: 'cover' };
const ellipsis = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
const NEON_GLOW = '0 1px 2px #000, 0 0 4px #00FFFF, 0 0 12px #00FFFF, 0 0 24px rgba(0,255,255,.6)';

export function ElementBody({ kind, style, data }) {
  const isEvent = kind === 'event';
  const Glyph = isEvent ? TicketIcon : PinIcon;
  const lead = isEvent ? 'GOING TO' : 'MEET AT';
  const title = data.title || '';
  const short = isEvent ? (data.short || title) : title;
  const caps = (isEvent ? title : title).toUpperCase();
  const shortCaps = short.toUpperCase();
  const dateLine = [data.mon && data.day ? `${data.mon} ${data.day}` : '', data.time].filter(Boolean).join(' · ');

  switch (style) {
    case 'mini':
      return (
        <div className="font-mono" style={{ display: 'flex', alignItems: 'center', gap: 7, background: 'rgba(0,0,0,.72)', borderRadius: 999, padding: '4px 12px 4px 4px', maxWidth: 230, color: '#fff' }}>
          <span style={{ width: 22, height: 22, flexShrink: 0, borderRadius: '50%', background: '#fff', color: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Glyph size={11} /></span>
          <span style={{ fontSize: 11, fontWeight: 700, ...ellipsis }}>{short}</span>
        </div>
      );
    case 'stub':
      return (
        <div className="font-mono" style={{ display: 'flex', alignItems: 'center', background: '#fff', color: '#000', borderRadius: 999, overflow: 'hidden', maxWidth: 240 }}>
          <span style={{ padding: '6px 10px 6px 14px', borderRight: '2px dashed #000', fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>
            {isEvent ? `${data.mon} ${data.day}` : 'MEET AT'}
          </span>
          <span style={{ padding: '6px 14px 6px 10px', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', ...ellipsis }}>{isEvent ? shortCaps : caps}</span>
        </div>
      );
    case 'tag':
      return (
        <div className="font-mono" style={{ display: 'flex', alignItems: 'center', gap: 8, maxWidth: 300, ...ring6, color: '#000', padding: '7px 14px', borderRadius: 999, fontSize: 11, fontWeight: 700, letterSpacing: isEvent ? '0.14em' : '0.18em', whiteSpace: 'nowrap' }}>
          <Glyph size={isEvent ? 14 : 13} />
          <span style={ellipsis}>{isEvent ? `GOING TO · ${caps}` : caps}</span>
        </div>
      );
    case 'date':
      return (
        <div className="font-mono" style={{ display: 'flex', alignItems: 'center', gap: 10, maxWidth: 280, color: '#fff', textShadow: '0 1px 6px rgba(0,0,0,.7)' }}>
          <span style={{ flexShrink: 0, width: 52, height: 52, borderRadius: '50%', background: '#fff', color: '#000', textShadow: 'none', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>
            <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.14em', color: '#FD199F' }}>{data.mon}</span>
            <span style={{ marginTop: 2, fontSize: 20, fontWeight: 800 }}>{data.day}</span>
          </span>
          <span style={{ minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: 15, fontWeight: 700, lineHeight: 1.2 }}>{title}</span>
            <span style={{ display: 'block', marginTop: 3, fontSize: 10, fontWeight: 700, letterSpacing: '0.12em' }}>
              {[data.time, (isEvent ? data.place : data.city || '').toUpperCase()].filter(Boolean).join(' · ')}
            </span>
          </span>
        </div>
      );
    case 'pin':
      return (
        <div className="font-mono" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, color: '#fff', textShadow: '0 1px 6px rgba(0,0,0,.8)' }}>
          <span aria-hidden style={{ position: 'relative', display: 'block', width: 48, height: 48 }}>
            <span style={{ position: 'absolute', inset: 0, borderRadius: '50%', border: '2px solid #00FFFF', animation: 'prcRipple 2.4s cubic-bezier(0,0,.2,1) infinite both' }} />
            <span style={{ position: 'absolute', inset: 0, borderRadius: '50%', ...ringBg, ...spin }} />
            <span style={{ position: 'absolute', inset: 4, borderRadius: '50%', background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Glyph size={18} /></span>
          </span>
          <span style={{ maxWidth: 220, fontSize: 12, fontWeight: 700, letterSpacing: '0.1em', background: 'rgba(0,0,0,.72)', padding: '3px 10px', borderRadius: 999, textShadow: 'none', ...ellipsis }}>{isEvent ? shortCaps : caps}</span>
        </div>
      );
    case 'ticket':
      return (
        <div className="font-mono" style={{ display: 'flex', alignItems: 'stretch', maxWidth: 290, background: '#fff', color: '#000', borderRadius: 999, overflow: 'hidden' }}>
          <div style={{ minWidth: 0, padding: '8px 14px 8px 20px', borderRight: '2px dashed #000' }}>
            <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.2em' }}>{isEvent ? 'ADMIT ONE · GOING TO' : 'MEET AT'}</div>
            <div style={{ marginTop: 3, fontSize: isEvent ? 14 : 15, fontWeight: 700, lineHeight: isEvent ? 1.15 : 1.1 }}>{title}</div>
            {data.meta && <div style={{ marginTop: 2, fontSize: isEvent ? 9 : 10, letterSpacing: isEvent ? '0.06em' : undefined }}>{data.meta}</div>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 16px 0 12px' }}><Glyph size={isEvent ? 14 : 18} /></div>
        </div>
      );
    case 'neon':
      return (
        <div className="font-mono" style={{ display: 'flex', flexDirection: 'column', gap: 4, maxWidth: 290 }}>
          <span style={{ alignSelf: 'flex-start', background: 'rgba(0,0,0,.78)', padding: '3px 10px', borderRadius: 999, fontSize: 10, fontWeight: 700, letterSpacing: '0.3em', color: '#FF66D6', textShadow: '0 0 6px #FF33C6' }}>{lead}</span>
          <span style={{ fontSize: 19, fontWeight: 800, lineHeight: 1.15, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#E8FFFF', textShadow: NEON_GLOW }}>{title}</span>
          {isEvent && dateLine && <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.16em', color: '#E8FFFF', textShadow: '0 1px 2px #000, 0 0 6px #00FFFF' }}>{dateLine}</span>}
        </div>
      );
    case 'poster':
      return (
        <div className="font-mono" style={{ display: 'flex', flexDirection: 'column', maxWidth: 250, background: '#000', color: '#fff' }}>
          {!isEvent && <span style={{ padding: '5px 12px', ...ring6, color: '#000', fontSize: 10, fontWeight: 800, letterSpacing: '0.16em' }}>MEET AT</span>}
          <span style={{ padding: isEvent ? '10px 12px 6px' : '8px 12px 10px', fontFamily: "'Bebas Neue', Impact, sans-serif", fontSize: 32, lineHeight: 0.92, letterSpacing: '0.02em', textTransform: 'uppercase' }}>{title}</span>
          {isEvent && <span style={{ padding: '5px 12px', ...ring6, color: '#000', fontSize: 10, fontWeight: 800, letterSpacing: '0.16em' }}>{dateLine}</span>}
        </div>
      );
    case 'sticker':
      return isEvent ? (
        <div className="font-mono" style={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 250, background: '#FAE1B4', color: '#000', padding: '8px 18px 9px', borderRadius: 999, boxShadow: '3px 3px 0 0 #000' }}>
          <span style={{ fontSize: 13, fontWeight: 800, lineHeight: 1.15 }}>{title}</span>
          {dateLine && <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.14em' }}>{dateLine}</span>}
        </div>
      ) : (
        <div className="font-mono" style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#FFCCE6', color: '#000', padding: '8px 18px 8px 14px', borderRadius: 999, boxShadow: '3px 3px 0 0 #000', fontSize: 13, fontWeight: 800 }}>
          <PinIcon size={15} />{title}
        </div>
      );
    case 'serif':
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxWidth: 300, textShadow: '0 1px 0 rgba(0,0,0,.6)' }}>
          <span className="font-mono" style={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 6, background: '#000', padding: '3px 10px', borderRadius: 999, fontSize: 9, fontWeight: 700, letterSpacing: '0.24em', color: '#FF33C6' }}>
            {!isEvent && <PinIcon size={11} />}{lead}
          </span>
          <span style={{ fontFamily: "'Times New Roman', Times, serif", fontStyle: 'italic', fontSize: isEvent ? 24 : 26, lineHeight: isEvent ? 1.15 : 1.05, color: '#fff' }}>
            <span style={{ background: '#000', padding: '0 8px 2px', WebkitBoxDecorationBreak: 'clone', boxDecorationBreak: 'clone' }}>{isEvent ? title : `at ${title}`}</span>
          </span>
        </div>
      );
    default:
      return (
        <div className="font-mono" style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'rgba(0,0,0,.72)', borderRadius: 999, padding: '6px 18px 6px 6px', maxWidth: isEvent ? 270 : undefined, color: '#fff' }}>
          <span aria-hidden style={{ position: 'relative', display: 'block', width: 34, height: 34, flexShrink: 0 }}>
            <span style={{ position: 'absolute', inset: 0, borderRadius: '50%', ...ringBg, ...(isEvent ? {} : spin) }} />
            <span style={{ position: 'absolute', inset: 3, borderRadius: '50%', background: isEvent ? '#fff' : '#000', color: isEvent ? '#000' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Glyph size={isEvent ? 14 : 13} /></span>
          </span>
          <div style={{ minWidth: 0 }}>
            {isEvent && <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.2em', color: '#FF33C6' }}>GOING TO</div>}
            <div style={{ marginTop: isEvent ? 2 : 0, fontSize: isEvent ? 13 : 14, fontWeight: 700, lineHeight: isEvent ? 1.2 : 1.15 }}>{title}</div>
            {data.meta && <div style={{ marginTop: 2, fontSize: isEvent ? 9 : 10, letterSpacing: isEvent ? '0.08em' : undefined, color: 'rgba(255,255,255,.75)' }}>{data.meta}</div>}
          </div>
        </div>
      );
  }
}

/**
 * One placed element: at its layout position, turned and scaled, with the editor's outline
 * when it is being dragged or is selected. `href` makes the viewer's event a link.
 */
const PortalElement = forwardRef(function PortalElement({
  kind,
  style,
  data,
  pos,
  rot = 0,
  scl = 1,
  dragging = false,
  selected = false,
  interactive = false,
  onPointerDown = null,
  href = null,
  children = null,
}, ref) {
  if (!data?.title) return null;
  const Tag = href && !interactive ? 'a' : 'div';
  const linkProps = Tag === 'a' ? { href, style: undefined } : {};
  return (
    <Tag
      ref={ref}
      {...linkProps}
      data-drag={kind}
      data-el={kind}
      data-testid={`portal-${kind}`}
      onPointerDown={onPointerDown || undefined}
      style={{
        position: 'absolute',
        left: `${pos.x}%`,
        top: `${pos.y}%`,
        maxWidth: '86%',
        padding: 6,
        boxSizing: 'border-box',
        borderRadius: 999,
        outline: dragging || selected ? '2px dashed #00FFFF' : '2px dashed transparent',
        outlineOffset: 2,
        transform: `rotate(${rot || 0}deg) scale(${scl || 1})`,
        transformOrigin: '50% 50%',
        cursor: interactive ? (dragging ? 'grabbing' : 'grab') : href ? 'pointer' : undefined,
        touchAction: interactive ? 'none' : undefined,
        zIndex: 4,
        userSelect: 'none',
        pointerEvents: interactive || href ? 'auto' : 'none',
        textDecoration: 'none',
        color: 'inherit',
      }}
    >
      {children}
      <ElementBody kind={kind} style={style} data={data} />
    </Tag>
  );
});

export default PortalElement;
